import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { HandsPraying, ChatCircle, Trash, Flag, PaperPlaneRight, Scales, ArrowUUpLeft, Funnel, CaretDown, SignIn } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useUndoableDelete } from "../lib/useUndoableDelete";

// Auto-advance pause coordination. The media players check
// `window.__hwCommenting` before auto-advancing. We keep it TRUE while a
// composer is focused AND for a few seconds after Send, so a freshly
// posted comment isn't wiped by the player jumping to the next track
// (which would refetch the thread against a different target_id).
let _hwPauseTimer = null;
function holdAutoAdvancePause() {
  try { window.__hwCommenting = true; } catch (_) {}
  if (_hwPauseTimer) { clearTimeout(_hwPauseTimer); _hwPauseTimer = null; }
}
function releaseAutoAdvancePause(delayMs = 800) {
  if (_hwPauseTimer) clearTimeout(_hwPauseTimer);
  _hwPauseTimer = setTimeout(() => {
    try { window.__hwCommenting = false; } catch (_) {}
    _hwPauseTimer = null;
  }, Math.max(0, delayMs));
}

// Judgment section — YouTube-style comments, branded for Holy Words.
//
// Drop this onto ANY page that wants a community thread:
//
//   <JudgmentSection targetType="movie" targetId={movie.id} title={movie.title} />
//   <JudgmentSection targetType="sermon" targetId={sermon.id} />
//   <JudgmentSection targetType="post" targetId={post.id} />
//
// Why "Judgment"? Self-aware label that echoes Matt 7:1 — community
// expression with a reminder to speak with grace. Functionally it's
// comments: top-level + one-level replies, like-counts, owner delete,
// and a report-for-moderation button.
//
// Auth model: anonymous users can READ. Posting / liking / deleting
// requires sign-in; we show a friendly "Sign in to add yours" prompt
// in place of the textarea for guests.
export default function JudgmentSection({
  targetType,
  targetId,
  title = "",
  accent = "terracotta",
  // The component is most often mounted INSIDE a modal player or a
  // narrow column on a profile page. We let the caller hint at the
  // width so we can size the avatar / text correctly without doing
  // CSS gymnastics from outside.
  variant = "default",
  // When true, the laptop (lg+) view is rendered as an illuminated
  // "open Bible" parchment page. Mobile/tablet keep the classic dark
  // thread. Players opt into this; content pages keep the classic look.
  scripture = false,
  // When true (with scripture), render ONLY the Bible page (no classic
  // thread) — used when the Bible panel lives in its own laptop column
  // next to "Up Next"; a separate classic instance covers mobile.
  bibleOnly = false,
}) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [draft, setDraft] = useState("");
  const [replyTo, setReplyTo] = useState(null);   // parent id we're replying to
  const [replyDraft, setReplyDraft] = useState("");
  const [sort, setSort] = useState("top");        // "top" | "new"
  const [openReplies, setOpenReplies] = useState({}); // {parentId: bool}
  const textareaRef = useRef(null);

  const fetchRows = useCallback(async () => {
    if (!targetId) return;
    setLoading(true);
    try {
      const { data } = await api.get(
        `/judgments?target_type=${encodeURIComponent(targetType)}&target_id=${encodeURIComponent(targetId)}&sort=${sort}`
      );
      setRows(Array.isArray(data) ? data : []);
    } catch (_) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [targetType, targetId, sort]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // Safety net: whenever this thread unmounts (player closes or the
  // track changes and remounts the section) force-release the
  // auto-advance pause so a stale focus can never permanently stop
  // continuous play.
  useEffect(() => () => { releaseAutoAdvancePause(0); }, []);

  // Safety: never leave the global "commenting" pause flag stuck on if
  // the section unmounts (player closed) while a box was focused.
  useEffect(() => () => { releaseAutoAdvancePause(0); }, []);

  // Split the flat backend response into parents + their replies. The
  // server returns replies grouped after each parent so we can rely on
  // `parent_id` to bucket. This avoids a second list-replies round trip.
  const { parents, repliesByParent } = useMemo(() => {
    const parents = [];
    const repliesByParent = {};
    for (const r of rows) {
      if (r.parent_id) {
        (repliesByParent[r.parent_id] ||= []).push(r);
      } else {
        parents.push(r);
      }
    }
    return { parents, repliesByParent };
  }, [rows]);

  const submitTopLevel = async () => {
    if (!user || posting) return;
    const text = draft.trim();
    if (!text) return;
    setPosting(true);
    holdAutoAdvancePause();
    try {
      const { data } = await api.post("/judgments", {
        target_type: targetType,
        target_id: targetId,
        text,
      });
      setRows((prev) => [data, ...prev]);
      setDraft("");
      toast.success("Judgment posted");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't post your judgment. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Couldn't post your judgment.");
    } finally {
      setPosting(false);
      releaseAutoAdvancePause(5000);
    }
  };

  const submitReply = async (parentId) => {
    if (!user || posting) return;
    const text = replyDraft.trim();
    if (!text) return;
    setPosting(true);
    holdAutoAdvancePause();
    try {
      const { data } = await api.post("/judgments", {
        target_type: targetType,
        target_id: targetId,
        text,
        parent_id: parentId,
      });
      // Insert reply right after its parent so the order stays sensible
      // without refetching.
      setRows((prev) => {
        const next = [...prev];
        const idx = next.findIndex((r) => r.id === parentId);
        if (idx >= 0) {
          // Bump parent's reply_count locally for the toggle label.
          next[idx] = { ...next[idx], reply_count: (next[idx].reply_count || 0) + 1 };
          // Place reply at the end of this parent's reply group.
          let insertAt = idx + 1;
          while (insertAt < next.length && next[insertAt].parent_id === parentId) insertAt++;
          next.splice(insertAt, 0, data);
        }
        return next;
      });
      setReplyDraft("");
      setReplyTo(null);
      setOpenReplies((s) => ({ ...s, [parentId]: true }));
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't post your reply. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Couldn't post your reply.");
    } finally {
      setPosting(false);
      releaseAutoAdvancePause(5000);
    }
  };

  const toggleLike = async (id) => {
    if (!user) return;
    // Optimistic flip — much snappier than waiting for the server.
    setRows((prev) =>
      prev.map((r) =>
        r.id === id
          ? {
              ...r,
              liked_by_me: !r.liked_by_me,
              like_count: r.like_count + (r.liked_by_me ? -1 : 1),
            }
          : r
      )
    );
    try { await api.post(`/judgments/${id}/like`); } catch (_) {}
  };

  const removeJudgment = useUndoableDelete({
    setList: setRows,
    keyOf: (r) => r.id,
    apiDelete: (id) => api.delete(`/judgments/${id}`),
    successLabel: "Judgment deleted",
  });
  const deleteRow = (id) => {
    if (!user) return;
    const target = rows.find((r) => r.id === id);
    // Strip replies optimistically too — same effect as the previous
    // immediate filter. Replies re-appear on next refresh if Undo.
    setRows((prev) => prev.filter((r) => r.parent_id !== id));
    removeJudgment(id, target);
  };

  const reportRow = async (id) => {
    if (!user) return;
    try {
      await api.post(`/judgments/${id}/report`);
      window?.alert?.("Flagged for review. Thank you.");
    } catch (_) {}
  };

  const total = parents.length + Object.values(repliesByParent).reduce((s, a) => s + a.length, 0);

  const classicView = (
    <section
      data-testid="judgment-section"
      className={
        variant === "bare"
          ? "w-full flex flex-col px-3 lg:px-0 bg-transparent lg:flex-1 lg:min-h-0 lg:overflow-hidden"
          : `w-full flex flex-col ${variant === "compact" ? "px-3" : "px-4 sm:px-6"} backdrop-blur rounded-2xl border border-cream/10 hw-player-deep`
      }
    >
      {variant !== "bare" && (
        <header className="sticky top-0 z-10 flex items-center justify-between gap-3 py-4 backdrop-blur border-b border-cream/10 -mx-4 sm:-mx-6 px-4 sm:px-6 hw-player-deep">
          <div className="flex items-center gap-2">
            <Scales size={18} weight="duotone" className="text-terracotta" />
            <h3 className="font-serif text-xl text-forest dark:text-cream">
              Judgments
              <span className="ml-2 text-[11px] tracking-[0.18em] uppercase text-forest/60 dark:text-cream/60 font-sans font-bold">
                {total} {total === 1 ? "voice" : "voices"}
              </span>
            </h3>
          </div>
          <button
            type="button"
            data-testid="judgment-sort-toggle"
            onClick={() => setSort((s) => (s === "top" ? "new" : "top"))}
            className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-forest/10 hover:bg-forest/20 text-forest dark:text-cream text-[11px] tracking-[0.18em] uppercase font-bold transition-all"
            title={`Sorted by ${sort === "top" ? "top" : "newest"} — tap to switch`}
          >
            <Funnel size={12} weight="bold" />
            <span>{sort === "top" ? "Top" : "Newest"}</span>
            <CaretDown size={11} weight="bold" />
          </button>
        </header>
      )}

      {/* List — sits above the bottom composer (YouTube-style). The
          composer was historically at the top, but the user asked for
          it to live at the bottom of the panel like YouTube's
          comments sheet so the most recent activity reads chronologically
          and the input is reachable with the thumb on mobile. */}
      <div className={`space-y-4 py-4 ${variant === "bare" ? "lg:flex-1 lg:overflow-y-auto lg:min-h-0" : ""}`}>
        {loading && (
          <div data-testid="judgment-loading" className="text-center py-6 text-forest/60 dark:text-cream/60 text-[12px] tracking-[0.18em] uppercase">
            Loading judgments…
          </div>
        )}
        {!loading && parents.length === 0 && (
          <div data-testid="judgment-empty" className="text-center py-8 text-forest/60 dark:text-cream/60">
            <Scales size={28} weight="duotone" className="mx-auto opacity-50 mb-2" />
            <div className="text-[12px] tracking-[0.18em] uppercase font-bold">Be the first to speak</div>
            <div className="text-[11px] opacity-70 mt-1">Your judgment opens the floor for the community.</div>
          </div>
        )}
        {!loading && parents.map((p) => {
          const replies = repliesByParent[p.id] || [];
          const isOpen = openReplies[p.id];
          return (
            <article key={p.id} data-testid={`judgment-row-${p.id}`} className="flex gap-3">
              <Avatar name={p.user_name} picture={p.user_picture} size={36} />
              <div className="flex-1 min-w-0">
                <RowHeader name={p.user_name} createdAt={p.created_at} />
                <p className="text-[14px] leading-relaxed text-forest dark:text-cream/95 whitespace-pre-wrap break-words mt-0.5">{p.text}</p>
                <RowActions
                  row={p}
                  onLike={() => toggleLike(p.id)}
                  onReply={user ? () => { setReplyTo(p.id); setReplyDraft(""); } : undefined}
                  onDelete={() => deleteRow(p.id)}
                  onReport={() => reportRow(p.id)}
                />

                {/* Reply composer — slides in when user taps "Reply" */}
                {replyTo === p.id && user && (
                  <div className="mt-3">
                    <Composer
                      value={replyDraft}
                      onChange={setReplyDraft}
                      onSubmit={() => submitReply(p.id)}
                      posting={posting}
                      placeholder={`Reply to ${p.user_name}…`}
                      user={user}
                      accent={accent}
                      onCancel={() => { setReplyTo(null); setReplyDraft(""); }}
                      compact
                    />
                  </div>
                )}

                {/* Replies toggle + thread */}
                {replies.length > 0 && (
                  <div className="mt-2">
                    <button
                      type="button"
                      data-testid={`judgment-replies-toggle-${p.id}`}
                      onClick={() => setOpenReplies((s) => ({ ...s, [p.id]: !s[p.id] }))}
                      className="inline-flex items-center gap-1 text-[11px] font-bold tracking-[0.18em] uppercase text-terracotta hover:text-terracotta-600 transition-colors"
                    >
                      <ArrowUUpLeft size={11} weight="bold" />
                      {isOpen ? "Hide replies" : `View ${replies.length} ${replies.length === 1 ? "reply" : "replies"}`}
                    </button>
                    {isOpen && (
                      <div className="mt-3 space-y-3 border-l-2 border-terracotta/20 pl-3">
                        {replies.map((r) => (
                          <article key={r.id} data-testid={`judgment-row-${r.id}`} className="flex gap-2">
                            <Avatar name={r.user_name} picture={r.user_picture} size={28} />
                            <div className="flex-1 min-w-0">
                              <RowHeader name={r.user_name} createdAt={r.created_at} small />
                              <p className="text-[13px] leading-relaxed text-forest dark:text-cream/95 whitespace-pre-wrap break-words mt-0.5">{r.text}</p>
                              <RowActions
                                row={r}
                                small
                                onLike={() => toggleLike(r.id)}
                                onReply={user ? () => {
                                  setReplyTo(p.id);
                                  setReplyDraft(`@${r.user_name} `);
                                } : undefined}
                                onDelete={() => deleteRow(r.id)}
                                onReport={() => reportRow(r.id)}
                              />
                            </div>
                          </article>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </div>

      {/* Bottom composer — sticky pinned to the bottom of the scroll
          container, exactly like YouTube's comment sheet. Sign-in
          gated; anonymous viewers see a tap-to-sign-in pill instead. */}
      <div className="sticky bottom-0 z-10 -mx-4 sm:-mx-6 lg:mx-0 px-4 sm:px-6 lg:px-0 py-3 backdrop-blur border-t border-cream/10 hw-player-deep lg:shrink-0">
        {user ? (
          <Composer
            value={draft}
            onChange={setDraft}
            onSubmit={submitTopLevel}
            posting={posting}
            placeholder={title ? `Speak your judgment on "${truncate(title, 40)}"…` : "Speak your judgment…"}
            user={user}
            ref={textareaRef}
            accent={accent}
            compact
          />
        ) : (
          <div className="rounded-xl border border-dashed border-terracotta/40 bg-terracotta/5 px-4 py-3 text-[13px] text-forest dark:text-cream/90 inline-flex items-center gap-2">
            <SignIn size={16} weight="bold" className="text-terracotta" />
            <span>Sign in to leave a judgment. Reading is open to everyone.</span>
          </div>
        )}
      </div>
    </section>
  );

  if (!scripture) return classicView;

  // ── LAPTOP "Illuminated Open Bible" view (lg+ only) ──────────────────
  const bibleView = (
    <div data-testid="judgments-laptop-panel" className="relative w-full h-full rounded-md bg-[var(--hw-accent)]/10 p-3 shadow-2xl flex">
      <div
        className="relative rounded-sm bg-cream overflow-hidden flex-1 flex flex-col min-h-0"
        style={{
          boxShadow: "3px 0 0 var(--hw-accent), 4px 0 0 var(--hw-accent-hover), -3px 0 0 var(--hw-accent), -4px 0 0 var(--hw-accent-hover)",
          backgroundImage:
            "radial-gradient(circle at 18% 12%, var(--hw-accent-soft), transparent 55%), radial-gradient(circle at 82% 88%, var(--hw-accent-soft), transparent 50%)",
        }}
      >
        {/* terracotta ribbon bookmark */}
        <div
          className="absolute top-0 right-10 w-5 h-24 bg-[var(--hw-accent)] z-20 shadow-md"
          style={{ clipPath: "polygon(0 0,100% 0,100% 100%,50% calc(100% - 10px),0 100%)" }}
          aria-hidden="true"
        />
        <div className="relative z-10 px-9 py-8 flex-1 overflow-y-auto min-h-0">
          <h3 className="font-serif text-2xl text-center text-ink uppercase tracking-[0.22em] pb-3 border-b-2 border-ink/15">
            Judgments
          </h3>
          <div className="flex items-center justify-between mt-3 mb-6">
            <span className="font-serif italic text-ink/50 text-[12px]">
              {total} {total === 1 ? "voice" : "voices"} on record
            </span>
            <div className="flex gap-4 font-serif text-[11px] tracking-widest uppercase">
              {["top", "new"].map((s) => (
                <button
                  key={s}
                  type="button"
                  data-testid="judgments-sort-toggle"
                  onClick={() => setSort(s)}
                  className={`pb-0.5 transition-colors ${sort === s ? "text-[var(--hw-accent)] border-b border-[var(--hw-accent)]" : "text-ink/45 hover:text-ink/70"}`}
                >
                  {s === "top" ? "Top" : "New"}
                </button>
              ))}
            </div>
          </div>

          {/* Composer — written at the head of the page */}
          <div className="relative border-y border-ink/15 py-5 mb-8">
            {user ? (
              <ScriptureComposer
                value={draft}
                onChange={setDraft}
                onSubmit={submitTopLevel}
                posting={posting}
                placeholder={title ? `Inscribe your judgment on "${truncate(title, 42)}"…` : "Inscribe your judgment…"}
                user={user}
                ref={textareaRef}
              />
            ) : (
              <div data-testid="judgments-signin-gate" className="flex items-center justify-center py-2">
                <span className="flex items-center gap-2 px-6 py-2 bg-[var(--hw-accent)] text-cream font-serif uppercase tracking-wider text-[11px] rounded-full shadow">
                  <SignIn size={14} weight="bold" /> Sign in to leave a judgment
                </span>
              </div>
            )}
          </div>

          {loading && (
            <div data-testid="judgment-loading" className="flex items-center justify-center py-12 animate-pulse text-ink/50 font-serif italic tracking-widest">
              Turning the pages…
            </div>
          )}

          {!loading && parents.length === 0 && (
            <div data-testid="judgment-empty" className="text-center py-12 px-6">
              <Scales size={56} weight="duotone" className="mx-auto text-[var(--hw-accent)]/60 mb-5" />
              <p className="font-serif text-2xl text-ink/75 leading-snug">
                <span className="float-left text-5xl font-serif text-[var(--hw-accent)] pr-2 leading-none">B</span>
                e the first to speak. Your judgment opens the floor for the congregation.
              </p>
            </div>
          )}

          {!loading && parents.map((p, i) => {
            const replies = repliesByParent[p.id] || [];
            const isOpen = openReplies[p.id];
            return (
              <div key={p.id} data-testid={`judgments-comment-verse-${p.id}`} className="relative pl-9 pr-1 py-3 group">
                <span className="absolute left-0 top-3 font-serif text-lg font-bold text-[var(--hw-accent)] select-none">{i + 1}</span>
                <div className="flex items-baseline gap-2">
                  <span className="font-serif font-bold uppercase text-[11px] tracking-wider text-ink">{p.user_name}</span>
                  <span className="font-serif italic text-ink/50 text-[11px]">{formatRelative(p.created_at)}</span>
                </div>
                <p className={`font-sans text-[14px] text-ink leading-relaxed mt-1 whitespace-pre-wrap break-words ${i === 0 ? "first-letter:float-left first-letter:text-5xl first-letter:font-serif first-letter:text-[var(--hw-accent)] first-letter:pr-2 first-letter:leading-none" : ""}`}>{p.text}</p>
                <div className="flex items-center gap-5 mt-2 opacity-70 group-hover:opacity-100 transition-opacity">
                  <button
                    type="button"
                    data-testid={`judgments-like-button-${p.id}`}
                    onClick={() => toggleLike(p.id)}
                    className={`inline-flex items-center gap-1.5 text-[12px] font-serif transition-transform ${p.liked_by_me ? "text-[var(--hw-accent)] scale-105" : "text-ink/70 hover:text-[var(--hw-accent)]"}`}
                  >
                    <HandsPraying size={15} weight={p.liked_by_me ? "fill" : "regular"} />
                    {p.like_count > 0 && <span>{p.like_count}</span>}
                  </button>
                  {user && (
                    <button type="button" data-testid={`judgments-reply-button-${p.id}`} onClick={() => { setReplyTo(p.id); setReplyDraft(""); }} className="text-[12px] font-serif italic text-[var(--hw-accent)] hover:underline">
                      Reply
                    </button>
                  )}
                  {p.mine ? (
                    <button type="button" onClick={() => deleteRow(p.id)} className="text-ink/30 hover:text-[var(--hw-accent)] transition-colors" title="Delete"><Trash size={14} /></button>
                  ) : (
                    <button type="button" onClick={() => reportRow(p.id)} className="text-ink/25 hover:text-[var(--hw-accent)] transition-colors" title="Report"><Flag size={14} /></button>
                  )}
                </div>

                {replyTo === p.id && user && (
                  <div className="mt-3 pl-5 border-l border-ink/20">
                    <ScriptureComposer value={replyDraft} onChange={setReplyDraft} onSubmit={() => submitReply(p.id)} posting={posting} placeholder={`Reply to ${p.user_name}…`} user={user} onCancel={() => { setReplyTo(null); setReplyDraft(""); }} compact />
                  </div>
                )}

                {replies.length > 0 && (
                  <div className="mt-2">
                    <button type="button" onClick={() => setOpenReplies((s) => ({ ...s, [p.id]: !s[p.id] }))} className="text-[11px] font-serif italic text-[var(--hw-accent)] hover:underline">
                      {isOpen ? "Hide replies" : `View ${replies.length} ${replies.length === 1 ? "reply" : "replies"}`}
                    </button>
                    {isOpen && (
                      <div className="ml-3 mt-3 pl-4 border-l border-ink/20 flex flex-col gap-4">
                        {replies.map((r) => (
                          <div key={r.id} data-testid={`judgments-comment-verse-${r.id}`} className="group/r">
                            <div className="flex items-baseline gap-2">
                              <span className="font-serif font-bold uppercase text-[10px] tracking-wider text-ink">{r.user_name}</span>
                              <span className="font-serif italic text-ink/50 text-[10px]">{formatRelative(r.created_at)}</span>
                            </div>
                            <p className="font-sans text-[13px] text-ink leading-relaxed mt-0.5 whitespace-pre-wrap break-words">{r.text}</p>
                            <div className="flex items-center gap-4 mt-1.5 opacity-70 group-hover/r:opacity-100 transition-opacity">
                              <button type="button" data-testid={`judgments-like-button-${r.id}`} onClick={() => toggleLike(r.id)} className={`inline-flex items-center gap-1 text-[11px] font-serif ${r.liked_by_me ? "text-[var(--hw-accent)]" : "text-ink/60 hover:text-[var(--hw-accent)]"}`}>
                                <HandsPraying size={13} weight={r.liked_by_me ? "fill" : "regular"} />
                                {r.like_count > 0 && <span>{r.like_count}</span>}
                              </button>
                              {user && (
                                <button type="button" onClick={() => { setReplyTo(p.id); setReplyDraft(`@${r.user_name} `); }} className="text-[11px] font-serif italic text-[var(--hw-accent)] hover:underline">Reply</button>
                              )}
                              {r.mine ? (
                                <button type="button" onClick={() => deleteRow(r.id)} className="text-ink/30 hover:text-[var(--hw-accent)]" title="Delete"><Trash size={12} /></button>
                              ) : (
                                <button type="button" onClick={() => reportRow(r.id)} className="text-ink/25 hover:text-[var(--hw-accent)]" title="Report"><Flag size={12} /></button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {bibleOnly ? (
        <div className="hidden lg:flex h-full">{bibleView}</div>
      ) : (
        <>
          <div className="hidden lg:block h-full">{bibleView}</div>
          <div className="lg:hidden">{classicView}</div>
        </>
      )}
    </>
  );
}

// ---- Pieces ---------------------------------------------------------

const Composer = React.forwardRef(function Composer(
  { value, onChange, onSubmit, posting, placeholder, user, onCancel, compact, accent = "terracotta" },
  ref
) {
  const handleKey = (e) => {
    // Cmd/Ctrl-Enter submits — same shortcut every social network uses.
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      onSubmit();
    }
  };
  return (
    <div className={`flex gap-3 ${compact ? "" : "mb-5"}`}>
      <Avatar name={user?.name || user?.email} picture={user?.avatar_url || user?.picture} size={compact ? 28 : 36} />
      <div className="flex-1">
        <div className="relative">
        <textarea
          ref={ref}
          data-testid={compact ? "judgment-reply-input" : "judgment-input"}
          value={value}
          onChange={(e) => {
            const v = e.target.value;
            onChange(v);
            // Only pause auto-advance while there's real text being
            // composed — an empty (idle) focused box must never stop
            // continuous play.
            if (v.trim()) holdAutoAdvancePause(); else releaseAutoAdvancePause(400);
          }}
          onKeyDown={handleKey}
          placeholder={placeholder}
          rows={compact ? 2 : 3}
          maxLength={1200}
          // The previous `focus:border-${accent}` template literals were
          // unreachable by Tailwind's JIT compiler — they produced no
          // styles at runtime. Switching to a CSS custom property
          // (--hw-accent, defined per theme in theme.css) gives us a
          // single source of truth that updates the moment the user
          // switches theme, no class recompile required. Sets focus
          // border + ring via onFocus/onBlur handlers since Tailwind's
          // peer/focus utilities can't read a CSS variable mid-class.
          className="w-full px-3 py-2 pr-12 rounded-xl bg-cream dark:bg-ink/60 border-2 outline-none text-[14px] text-forest dark:text-cream placeholder:text-forest/40 dark:placeholder:text-cream/40 resize-none transition-all"
          style={{ borderColor: "rgba(42, 75, 66, 0.15)" }}
          onFocus={(e) => {
            // Pause auto-advance on focus ONLY if mid-composition.
            if ((value || "").trim()) holdAutoAdvancePause();
            e.currentTarget.style.borderColor = "var(--hw-accent)";
            e.currentTarget.style.boxShadow = "0 0 0 3px var(--hw-accent-soft)";
          }}
          onBlur={(e) => {
            releaseAutoAdvancePause(800);
            e.currentTarget.style.borderColor = "rgba(42, 75, 66, 0.15)";
            e.currentTarget.style.boxShadow = "none";
          }}
        />
          <button
            type="button"
            onClick={onSubmit}
            disabled={posting || !value.trim()}
            data-testid={compact ? "judgment-reply-submit" : "judgment-submit"}
            aria-label={compact ? "Send reply" : "Post judgment"}
            title="Send"
            className="absolute right-2 bottom-2 w-9 h-9 rounded-full grid place-items-center text-cream disabled:opacity-40 disabled:cursor-not-allowed shadow-md transition-all"
            style={{ backgroundColor: "var(--hw-accent)" }}
            onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = "var(--hw-accent-hover)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "var(--hw-accent)"; }}
          >
            <PaperPlaneRight size={16} weight="fill" />
          </button>
        </div>
        <div className="flex items-center justify-between mt-1.5">
          <span className="text-[10px] tracking-[0.18em] uppercase text-forest/50 dark:text-cream/50">
            {value.length}/1200 · ⌘/Ctrl+Enter to post
          </span>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              data-testid="judgment-cancel-reply"
              className="px-3 h-8 rounded-full bg-forest/10 hover:bg-forest/20 text-forest dark:text-cream text-[11px] tracking-[0.18em] uppercase font-bold transition-all"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

const ScriptureComposer = React.forwardRef(function ScriptureComposer(
  { value, onChange, onSubmit, posting, placeholder, user, onCancel, compact },
  ref
) {
  const handleKey = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); onSubmit(); }
  };
  return (
    <div className="flex gap-3 items-start">
      <Avatar name={user?.name || user?.email} picture={user?.avatar_url || user?.picture} size={compact ? 24 : 30} />
      <div className="flex-1 relative">
        <textarea
          ref={ref}
          data-testid={compact ? "judgments-reply-composer-input" : "judgments-composer-input"}
          value={value}
          onChange={(e) => { const v = e.target.value; onChange(v); if (v.trim()) holdAutoAdvancePause(); else releaseAutoAdvancePause(400); }}
          onKeyDown={handleKey}
          onFocus={() => { if ((value || "").trim()) holdAutoAdvancePause(); }}
          onBlur={() => releaseAutoAdvancePause(800)}
          placeholder={placeholder}
          rows={compact ? 2 : 3}
          maxLength={1200}
          className="hw-scripture-input w-full border-none outline-none resize-none font-sans text-[14px] pr-10 rounded-sm placeholder:font-serif placeholder:italic"
          style={{ boxShadow: "inset 0 -1px 0 rgba(26,26,26,0.18)", padding: "6px 8px" }}
        />
        <button
          type="button"
          onClick={onSubmit}
          disabled={posting || !value.trim()}
          data-testid={compact ? "judgments-reply-composer-send" : "judgments-composer-send"}
          aria-label="Post judgment"
          title="Send"
          className="absolute bottom-1 right-0 text-[var(--hw-accent)] disabled:opacity-30 hover:scale-110 transition-transform"
        >
          <PaperPlaneRight size={20} weight="fill" />
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="mt-1 block text-[10px] font-serif italic text-ink/40 hover:text-[var(--hw-accent)]">Cancel</button>
        )}
      </div>
    </div>
  );
});

function Avatar({ name = "?", picture = "", size = 36 }) {
  const initial = (name || "?").trim().charAt(0).toUpperCase() || "?";
  // Backend-relative paths (e.g. "/api/users/{id}/avatar") need the
  // REACT_APP_BACKEND_URL prefix when the frontend is served from a
  // different origin (custom prod domain, Vercel, etc.). Absolute URLs
  // (https://…, data:…) and empty strings are passed through.
  const src = picture && !/^(https?:|data:)/i.test(picture)
    ? `${process.env.REACT_APP_BACKEND_URL}${picture}`
    : picture;
  if (src) {
    return (
      <img
        src={src}
        alt={name}
        width={size}
        height={size}
        className="rounded-full object-cover shrink-0 border border-forest/15"
        style={{ width: size, height: size }}
        onError={(e) => { try { e.currentTarget.style.display = "none"; } catch (_) {} }}
      />
    );
  }
  return (
    <div
      className="rounded-full grid place-items-center shrink-0 bg-gradient-to-br from-forest to-forest/70 text-cream font-bold"
      style={{ width: size, height: size, fontSize: Math.floor(size * 0.4) }}
      aria-hidden="true"
    >
      {initial}
    </div>
  );
}

function RowHeader({ name, createdAt, small }) {
  return (
    <div className={`flex items-baseline gap-2 ${small ? "text-[11px]" : "text-[12px]"}`}>
      <span className="font-bold text-forest dark:text-cream">{name}</span>
      <span className="text-forest/50 dark:text-cream/50">·</span>
      <span className="text-forest/50 dark:text-cream/50">{formatRelative(createdAt)}</span>
    </div>
  );
}

function RowActions({ row, onLike, onReply, onDelete, onReport, small }) {
  const sz = small ? 12 : 14;
  return (
    <div className="flex items-center gap-1 mt-1.5 -ml-1">
      <button
        type="button"
        onClick={onLike}
        data-testid={`judgment-like-${row.id}`}
        aria-label={row.liked_by_me ? "Take back prayer" : "Pray for this"}
        title={row.liked_by_me ? "Take back prayer" : "Pray for this"}
        className={`inline-flex items-center gap-1 px-2 h-7 rounded-full text-[11px] font-bold transition-all ${
          row.liked_by_me
            ? "text-terracotta bg-terracotta/10"
            : "text-forest/60 dark:text-cream/60 hover:text-terracotta hover:bg-terracotta/5"
        }`}
      >
        <HandsPraying size={sz} weight={row.liked_by_me ? "fill" : "regular"} />
        {row.like_count > 0 && <span>{row.like_count}</span>}
      </button>
      {onReply && (
        <button
          type="button"
          onClick={onReply}
          data-testid={`judgment-reply-${row.id}`}
          aria-label="Reply"
          title="Reply"
          className="inline-flex items-center gap-1 px-2 h-7 rounded-full text-[11px] font-bold text-forest/60 dark:text-cream/60 hover:text-forest hover:bg-forest/5 transition-all"
        >
          <ChatCircle size={sz} weight="regular" />
          <span>Reply</span>
        </button>
      )}
      {row.mine ? (
        <button
          type="button"
          onClick={onDelete}
          data-testid={`judgment-delete-${row.id}`}
          aria-label="Delete"
          title="Delete"
          className="inline-flex items-center gap-1 px-2 h-7 rounded-full text-[11px] font-bold text-forest/60 dark:text-cream/60 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-all"
        >
          <Trash size={sz} weight="regular" />
        </button>
      ) : (
        <button
          type="button"
          onClick={onReport}
          data-testid={`judgment-report-${row.id}`}
          aria-label="Report"
          title="Report"
          className="inline-flex items-center gap-1 px-2 h-7 rounded-full text-[11px] font-bold text-forest/40 dark:text-cream/40 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-950/30 transition-all"
        >
          <Flag size={sz} weight="regular" />
        </button>
      )}
    </div>
  );
}

function truncate(s, n) {
  if (!s) return "";
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

// "2 min ago", "3 d ago", "Jun 12" — same vocabulary Twitter / YT use.
function formatRelative(iso) {
  if (!iso) return "";
  try {
    const t = new Date(iso).getTime();
    if (!Number.isFinite(t)) return "";
    const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    if (s < 86400 * 7) return `${Math.floor(s / 86400)}d ago`;
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch (_) { return ""; }
}
